import { api } from './client'
import type { ApiEnvelope, PaginatedResponse, VersionItem } from '@/types/api'

export interface VersionQueryParams {
    versionName?: string
    status?: number | string
    fromDate?: string
    toDate?: string
}

interface SpringPage<T> {
    content: T[]
    totalElements: number
    totalPages: number
    number: number
    size: number
}

function adaptPage<T>(raw: SpringPage<T> | PaginatedResponse<T>): PaginatedResponse<T> {
    if ('items' in raw) return raw
    return {
        items: raw.content,
        total: raw.totalElements || 0,
        page: (raw.number || 0) + 1,
        limit: raw.size || 10,
        totalPages: raw.totalPages || 0,
    }
}

export async function fetchVersions(
    page: number = 0,
    pageSize: number = 10,
    params: VersionQueryParams = {},
    signal?: AbortSignal
): Promise<PaginatedResponse<VersionItem>> {
    try {
        const response = await api.get<ApiEnvelope<SpringPage<VersionItem>>>('/v1/version-management', {
            params: { page, size: pageSize, ...params },
            signal
        })
        return adaptPage(response.data.responseData)
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi lấy danh sách phiên bản.';
        throw new Error(message);
    }
}

export async function getVersionById(id: string, signal?: AbortSignal): Promise<VersionItem> {
    try {
        const response = await api.get<ApiEnvelope<VersionItem>>(`/v1/version-management/${id}`, { signal })
        return response.data.responseData
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi lấy chi tiết phiên bản.';
        throw new Error(message);
    }
}

export async function createVersion(data: any): Promise<ApiEnvelope<VersionItem>> {
    try {
        const formData = new FormData()
        Object.entries(data).forEach(([key, value]) => {
            if (value !== null && value !== undefined) {
                formData.append(key, value as any)
            }
        })

        const response = await api.post<ApiEnvelope<VersionItem>>('/v1/version-management', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        })
        return response.data
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi tạo phiên bản.';
        throw new Error(message);
    }
}

export async function updateVersion(id: string, data: any): Promise<ApiEnvelope<VersionItem>> {
    try {
        const formData = new FormData()
        Object.entries(data).forEach(([key, value]) => {
            if (value !== null && value !== undefined) {
                formData.append(key, value as any)
            }
        })

        const response = await api.put<ApiEnvelope<VersionItem>>(`/v1/version-management/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        })
        return response.data
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi cập nhật phiên bản.';
        throw new Error(message);
    }
}

export async function deleteVersion(id: string): Promise<ApiEnvelope<any>> {
    try {
        const response = await api.delete<ApiEnvelope<any>>(`/v1/version-management/${id}`)
        return response.data
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi xóa phiên bản.';
        throw new Error(message);
    }
}